import { useState, useCallback } from 'react';
import styles from './ApiDocsPage.module.css';

const REQUIRED_FIELDS = [
  { name: 'firstName', type: 'string', description: 'First name (1-50 chars)' },
  { name: 'lastName', type: 'string', description: 'Last name (1-50 chars)' },
  { name: 'email', type: 'string', description: 'Valid email address' },
  { name: 'phone', type: 'string', description: 'Phone number (7-20 chars)' },
];

const OPTIONAL_FIELDS = [
  { name: 'dateOfBirth', type: 'string', description: 'Date of birth (YYYY-MM-DD). Optional at create, but the member cannot complete checkout until it is on file — send it here to spare them the extra step.' },
  { name: 'gender', type: 'string', description: 'male, female, or other' },
  { name: 'memberId', type: 'string', description: 'External member ID (max 100 chars)' },
  { name: 'planCode', type: 'string', description: 'Membership plan code (e.g., ACCESS, PLUS, ONE, PLUS_FAMILY, ONE_MEMBER_CHILD). Use GET /api/partner/plan-codes for all available codes. Default: ACCESS' },
  { name: 'membershipStatus', type: 'string', description: 'active or inactive (default: active)' },
  { name: 'street', type: 'string', description: 'Street address (max 200 chars)' },
  { name: 'city', type: 'string', description: 'City (max 100 chars)' },
  { name: 'state', type: 'string', description: 'State (max 50 chars)' },
  { name: 'zipCode', type: 'string', description: 'US zip code (e.g., 12345 or 12345-6789)' },
  { name: 'tpaName', type: 'string', description: 'Third-party administrator name' },
  { name: 'writingAgentName', type: 'string', description: 'Writing agent name' },
  { name: 'membershipStartDate', type: 'string', description: 'Membership start date (YYYY-MM-DD)' },
  { name: 'membershipExpiresOn', type: 'string', description: 'Membership expiry date (YYYY-MM-DD)' },
  { name: 'membershipActivationDate', type: 'string', description: 'Membership activation date (YYYY-MM-DD). Send null or "" to clear a previously set date.' },
  { name: 'membershipInactivationDate', type: 'string', description: 'Membership inactivation date (YYYY-MM-DD). Send null or "" to clear a previously set date.' },
  { name: 'membershipCancellationDate', type: 'string', description: 'Membership cancellation date (YYYY-MM-DD). Send null or "" to clear a previously set date.' },
];

const SSO_QUERY_PARAMS = [
  { name: 'token', type: 'string', description: 'HMAC-SHA256 signature (hex-encoded)' },
  { name: 'timestamp', type: 'string', description: 'Unix timestamp in seconds (must be within 5 minutes of current time)' },
  { name: 'agency', type: 'string', description: 'Agency code (e.g., AOB475)' },
  { name: 'email', type: 'string', description: 'Email address of the user to authenticate' },
];

const SSO_ERROR_CODES = [
  { code: 'invalid_request', description: 'Missing required query parameters (token, timestamp, agency, or email)' },
  { code: 'invalid_signature', description: 'HMAC token does not match. Check your signing secret and payload format.' },
  { code: 'expired_timestamp', description: 'Timestamp is more than 5 minutes old or in the future. Generate a fresh URL.' },
  { code: 'agency_not_found', description: 'No agency found with the provided agency code.' },
  { code: 'sso_disabled', description: 'SSO is not enabled for this agency, or no SSO secret is configured.' },
  { code: 'internal_error', description: 'An unexpected server error occurred. Contact support.' },
];

const EXAMPLE_REQUEST = `{
  "firstName": "Jane",
  "lastName": "Smith",
  "email": "jane.smith@example.com",
  "dateOfBirth": "1990-05-15",
  "phone": "5551234567",
  "gender": "female",
  "planCode": "PLUS",
  "membershipStatus": "active",
  "street": "123 Main St",
  "city": "Austin",
  "state": "TX",
  "zipCode": "78701"
}`;

const CURL_EXAMPLE = `curl -X POST https://ovalcare.com/api/partner/members \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your-api-key-here" \\
  -d '{
    "firstName": "Jane",
    "lastName": "Smith",
    "email": "jane.smith@example.com",
    "dateOfBirth": "1990-05-15",
    "phone": "5551234567",
    "planCode": "PLUS"
  }'`;

const SUCCESS_RESPONSE = `// 201 Created (new member)
{
  "success": true,
  "member": {
    "id": "a1b2c3d4-e5f6-...",
    "email": "jane.smith@example.com",
    "status": "created",
    "plan": "PLUS"
  }
}

// 200 OK (existing member updated)
{
  "success": true,
  "member": {
    "id": "a1b2c3d4-e5f6-...",
    "email": "jane.smith@example.com",
    "status": "updated",
    "plan": "PLUS"
  }
}`;

const ERROR_RESPONSES = `// 401 Unauthorized — Missing or invalid API key
{
  "success": false,
  "error": "Missing X-API-Key header"
}

// 400 Bad Request — Validation errors
{
  "success": false,
  "error": "Validation failed",
  "details": [
    { "field": "email", "message": "Must be a valid email address" },
    { "field": "dateOfBirth", "message": "Date of birth must be in YYYY-MM-DD format" }
  ]
}

// 500 Internal Server Error
{
  "success": false,
  "error": "Internal server error"
}`;

const SSO_CURL_EXAMPLE = `# Generate the HMAC token
EMAIL="user@partner.com"
TIMESTAMP=$(date +%s)
AGENCY="AOB475"
SECRET="your-sso-secret-here"

TOKEN=$(echo -n "$EMAIL:$TIMESTAMP:$AGENCY" | \\
  openssl dgst -sha256 -hmac "$SECRET" -hex | awk '{print $NF}')

# Redirect user to this URL
echo "https://ovalcare.com/api/sso?token=$TOKEN&timestamp=$TIMESTAMP&agency=$AGENCY&email=$EMAIL"`;

const SSO_PYTHON_EXAMPLE = `import hmac
import hashlib
import time
import urllib.parse

email = "user@partner.com"
timestamp = str(int(time.time()))
agency_code = "AOB475"
sso_secret = "your-sso-secret-here"

# Sign: email:timestamp:agency_code
payload = f"{email}:{timestamp}:{agency_code}"
token = hmac.new(
    sso_secret.encode(),
    payload.encode(),
    hashlib.sha256
).hexdigest()

# Build the SSO URL
params = urllib.parse.urlencode({
    "token": token,
    "timestamp": timestamp,
    "agency": agency_code,
    "email": email,
})
url = f"https://ovalcare.com/api/sso?{params}"`;

const SSO_NODE_EXAMPLE = `import crypto from 'crypto';

const email = 'user@partner.com';
const timestamp = Math.floor(Date.now() / 1000).toString();
const agencyCode = 'AOB475';
const ssoSecret = 'your-sso-secret-here';

// Sign: email:timestamp:agency_code
const payload = \`\${email}:\${timestamp}:\${agencyCode}\`;
const token = crypto
  .createHmac('sha256', ssoSecret)
  .update(payload)
  .digest('hex');

// Build the SSO URL
const params = new URLSearchParams({ token, timestamp, agency: agencyCode, email });
const url = \`https://ovalcare.com/api/sso?\${params}\`;`;


const CRUD_QUERY_PARAMS = [
  { name: 'search', type: 'string', description: 'Search by first name, last name, or email (partial match)' },
  { name: 'memberId', type: 'string', description: 'Filter by exact external member ID' },
  { name: 'status', type: 'string', description: 'Filter by status: active or inactive' },
  { name: 'page', type: 'number', description: 'Page number (default: 1)' },
  { name: 'limit', type: 'number', description: 'Results per page (default: 20, max: 100)' },
];

const CRUD_UPDATE_FIELDS = [
  { name: 'firstName', type: 'string', description: 'First name (1-50 chars)' },
  { name: 'lastName', type: 'string', description: 'Last name (1-50 chars)' },
  { name: 'phone', type: 'string', description: 'Phone number' },
  { name: 'dateOfBirth', type: 'string', description: 'Date of birth (YYYY-MM-DD)' },
  { name: 'gender', type: 'string', description: 'male, female, or other' },
  { name: 'memberId', type: 'string', description: 'External member ID' },
  { name: 'planCode', type: 'string', description: 'Membership plan code (e.g., ACCESS, PLUS, ONE, PLUS_FAMILY). Use GET /api/partner/plan-codes for all available codes' },
  { name: 'membershipStatus', type: 'string', description: 'active or inactive' },
  { name: 'street', type: 'string', description: 'Street address' },
  { name: 'city', type: 'string', description: 'City' },
  { name: 'state', type: 'string', description: 'State' },
  { name: 'zipCode', type: 'string', description: 'US zip code' },
  { name: 'tpaName', type: 'string', description: 'Third-party administrator name' },
  { name: 'writingAgentName', type: 'string', description: 'Writing agent name' },
  { name: 'membershipStartDate', type: 'string', description: 'Start date (YYYY-MM-DD)' },
  { name: 'membershipExpiresOn', type: 'string', description: 'Expiry date (YYYY-MM-DD)' },
];

const CRUD_LIST_CURL = `curl -X GET "https://ovalcare.com/api/partner/members?search=Jane&status=active&page=1&limit=20" \\
  -H "X-API-Key: your-api-key-here"`;

const CRUD_LIST_RESPONSE = `{
  "success": true,
  "members": [
    {
      "id": "a1b2c3d4-e5f6-...",
      "email": "jane.smith@example.com",
      "firstName": "Jane",
      "lastName": "Smith",
      "phone": "5551234567",
      "dateOfBirth": "1990-05-15",
      "gender": "female",
      "memberId": "MEM-001",
      "plan": "PLUS",
      "status": "active",
      "address": {
        "street": "123 Main St",
        "city": "Austin",
        "state": "TX",
        "zipCode": "78701"
      },
      "createdAt": "2026-04-01T12:00:00.000Z",
      "updatedAt": "2026-04-02T08:30:00.000Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "totalPages": 1
  }
}`;

const CRUD_GET_CURL = `curl -X GET "https://ovalcare.com/api/partner/members/a1b2c3d4-e5f6-..." \\
  -H "X-API-Key: your-api-key-here"`;

const CRUD_GET_RESPONSE = `{
  "success": true,
  "member": {
    "id": "a1b2c3d4-e5f6-...",
    "email": "jane.smith@example.com",
    "firstName": "Jane",
    "lastName": "Smith",
    "phone": "5551234567",
    "dateOfBirth": "1990-05-15",
    "gender": "female",
    "memberId": "MEM-001",
    "plan": "PLUS",
    "status": "active",
    "address": {
      "street": "123 Main St",
      "city": "Austin",
      "state": "TX",
      "zipCode": "78701"
    },
    "createdAt": "2026-04-01T12:00:00.000Z",
    "updatedAt": "2026-04-02T08:30:00.000Z"
  }
}`;

const CRUD_UPDATE_CURL = `curl -X PUT "https://ovalcare.com/api/partner/members/a1b2c3d4-e5f6-..." \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your-api-key-here" \\
  -d '{
    "firstName": "Janet",
    "phone": "5559876543",
    "planCode": "PREMIUM"
  }'`;

const CRUD_UPDATE_RESPONSE = `{
  "success": true,
  "member": {
    "id": "a1b2c3d4-e5f6-...",
    "email": "jane.smith@example.com",
    "status": "updated",
    "plan": "PREMIUM"
  }
}`;

const CRUD_DELETE_CURL = `curl -X DELETE "https://ovalcare.com/api/partner/members/a1b2c3d4-e5f6-..." \\
  -H "X-API-Key: your-api-key-here"`;

const CRUD_DELETE_RESPONSE = `{
  "success": true,
  "member": {
    "id": "a1b2c3d4-e5f6-...",
    "email": "jane.smith@example.com",
    "status": "deleted"
  }
}`;

const CRUD_ERROR_CODES = [
  { code: '401', description: 'Missing or invalid X-API-Key header' },
  { code: '400', description: 'Validation failed — check the details array for specific field errors' },
  { code: '404', description: 'Member not found (or belongs to a different agency)' },
  { code: '429', description: 'Rate limit exceeded (100 requests/min per API key)' },
  { code: '500', description: 'Internal server error' },
];

const CodeBlock = ({ children, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    const text = typeof children === 'string' ? children : '';
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [children]);

  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeHeader}>
        {label && <span className={styles.codeLabel}>{label}</span>}
        <button className={styles.copyBtn} onClick={handleCopy} type="button">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className={styles.codePre}><code>{children}</code></pre>
    </div>
  );
};

const FieldTable = ({ fields, title }) => (
  <div className={styles.tableSection}>
    <h3 className={styles.tableTitle}>{title}</h3>
    <div className={styles.tableWrapper}>
      <table className={styles.fieldTable}>
        <thead>
          <tr>
            <th>Field</th>
            <th>Type</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {fields.map(({ name, type, description }) => (
            <tr key={name}>
              <td><code className={styles.fieldName}>{name}</code></td>
              <td><code className={styles.fieldType}>{type}</code></td>
              <td>{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ErrorCodeTable = ({ codes }) => (
  <div className={styles.tableSection}>
    <h3 className={styles.tableTitle}>Error Codes</h3>
    <div className={styles.tableWrapper}>
      <table className={styles.fieldTable}>
        <thead>
          <tr>
            <th>Code</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {codes.map(({ code, description }) => (
            <tr key={code}>
              <td><code className={styles.fieldName}>{code}</code></td>
              <td>{description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ApiDocsPage = () => (
  <div className={styles.container}>
    <div className={styles.content}>
      <header className={styles.header}>
        <div className={styles.badge}>Partner API</div>
        <h1 className={styles.title}>API Documentation</h1>
        <p className={styles.subtitle}>
          Integrate member enrollment and single sign-on with the Oval Care platform.
        </p>
      </header>

      {/* Base URL */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Base URL</h2>
        <CodeBlock label="Base URL">https://ovalcare.com/api</CodeBlock>
      </section>

      {/* Authentication */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Authentication</h2>
        <p className={styles.text}>
          Member API requests require an API key sent via the <code className={styles.inlineCode}>X-API-Key</code> header.
          Contact your Oval Care account manager to receive your API key.
        </p>
        <CodeBlock label="Header">X-API-Key: your-api-key-here</CodeBlock>
      </section>

      {/* Rate Limiting */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Rate Limiting</h2>
        <p className={styles.text}>
          Member API requests are limited to <strong>100 per minute</strong> per API key.
          SSO requests are limited to <strong>20 per minute</strong> per IP address.
          Exceeding these limits returns a <code className={styles.inlineCode}>429 Too Many Requests</code> response.
        </p>
      </section>

      {/* ==================== MEMBER API ==================== */}
      <section className={styles.section}>
        <div className={styles.sectionDivider}>Member API</div>
        <h2 className={styles.sectionTitle}>Create / Update Member</h2>

        <div className={styles.endpoint}>
          <div className={styles.endpointHeader}>
            <span className={styles.methodBadge}>POST</span>
            <code className={styles.endpointPath}>/partner/members</code>
          </div>
          <p className={styles.text}>
            Create or update a member. If a member with the given email already exists,
            their record will be updated with the provided fields (upsert behavior).
          </p>
        </div>

        <FieldTable title="Required Fields" fields={REQUIRED_FIELDS} />
        <FieldTable title="Optional Fields" fields={OPTIONAL_FIELDS} />
      </section>

      {/* Member Examples */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Member API Examples</h2>
        <CodeBlock label="curl">{CURL_EXAMPLE}</CodeBlock>
        <CodeBlock label="JSON Request Body">{EXAMPLE_REQUEST}</CodeBlock>
        <CodeBlock label="Success Responses">{SUCCESS_RESPONSE}</CodeBlock>
        <CodeBlock label="Error Responses">{ERROR_RESPONSES}</CodeBlock>
      </section>

      {/* Behavior Notes */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Member API Notes</h2>
        <ul className={styles.notesList}>
          <li>If the email already exists, the member is updated (not duplicated).</li>
          <li>On update, only non-null provided fields are applied — existing data is preserved.</li>
          <li>New members receive a random password and must use the password reset flow to log in.</li>
          <li>The <code className={styles.inlineCode}>planCode</code> defaults to ACCESS if not provided.</li>
          <li>The <code className={styles.inlineCode}>membershipStatus</code> defaults to active if not provided.</li>
          <li>All timestamps should be in YYYY-MM-DD format.</li>
        </ul>
      </section>


      {/* ==================== CRUD ENDPOINTS ==================== */}
      <section className={styles.section}>
        <div className={styles.sectionDivider}>Member CRUD Endpoints</div>
        <h2 className={styles.sectionTitle}>List / Search Members</h2>

        <div className={styles.endpoint}>
          <div className={styles.endpointHeader}>
            <span className={`${styles.methodBadge} ${styles.methodGet}`}>GET</span>
            <code className={styles.endpointPath}>/partner/members</code>
          </div>
          <p className={styles.text}>
            Retrieve a paginated list of members belonging to your agency. Supports search and filtering.
          </p>
        </div>

        <FieldTable title="Query Parameters" fields={CRUD_QUERY_PARAMS} />
        <CodeBlock label="curl">{CRUD_LIST_CURL}</CodeBlock>
        <CodeBlock label="Response">{CRUD_LIST_RESPONSE}</CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Get Member</h2>

        <div className={styles.endpoint}>
          <div className={styles.endpointHeader}>
            <span className={`${styles.methodBadge} ${styles.methodGet}`}>GET</span>
            <code className={styles.endpointPath}>/partner/members/:id</code>
          </div>
          <p className={styles.text}>
            Retrieve full details for a single member by their Oval user ID.
          </p>
        </div>

        <CodeBlock label="curl">{CRUD_GET_CURL}</CodeBlock>
        <CodeBlock label="Response">{CRUD_GET_RESPONSE}</CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Update Member</h2>

        <div className={styles.endpoint}>
          <div className={styles.endpointHeader}>
            <span className={`${styles.methodBadge} ${styles.methodPut}`}>PUT</span>
            <code className={styles.endpointPath}>/partner/members/:id</code>
          </div>
          <p className={styles.text}>
            Update one or more fields on an existing member. Only the fields you include
            in the request body will be changed — all other fields are preserved.
            At least one field is required.
          </p>
        </div>

        <FieldTable title="Updatable Fields (all optional, at least one required)" fields={CRUD_UPDATE_FIELDS} />
        <CodeBlock label="curl">{CRUD_UPDATE_CURL}</CodeBlock>
        <CodeBlock label="Response">{CRUD_UPDATE_RESPONSE}</CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Delete Member</h2>

        <div className={styles.endpoint}>
          <div className={styles.endpointHeader}>
            <span className={`${styles.methodBadge} ${styles.methodDelete}`}>DELETE</span>
            <code className={styles.endpointPath}>/partner/members/:id</code>
          </div>
          <p className={styles.text}>
            Soft-delete a member. Sets their status to inactive. The member record is
            preserved in the database but will no longer appear in active member queries.
          </p>
        </div>

        <CodeBlock label="curl">{CRUD_DELETE_CURL}</CodeBlock>
        <CodeBlock label="Response">{CRUD_DELETE_RESPONSE}</CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Security</h2>
        <p className={styles.text}>
          All CRUD endpoints are scoped to your agency. You can only access members that
          were created through your API key. Attempting to access a member that belongs to
          a different agency returns a <code className={styles.inlineCode}>404</code> response
          (not <code className={styles.inlineCode}>403</code>), so no information about other
          agencies' members is leaked.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>CRUD Error Codes</h2>
        <ErrorCodeTable codes={CRUD_ERROR_CODES} />
      </section>

      {/* ==================== SSO ==================== */}
      <section className={styles.section}>
        <div className={styles.sectionDivider}>Single Sign-On (SSO)</div>
        <h2 className={styles.sectionTitle}>Overview</h2>
        <p className={styles.text}>
          SSO allows your users to access the Oval Care admin panel directly from your
          application without a separate login. You generate a signed URL on your server,
          then redirect the user to it. Oval validates the signature and creates a session.
        </p>
        <p className={styles.text}>
          SSO must be enabled for your agency by an Oval administrator. Once enabled, you
          will receive an SSO secret used to sign authentication tokens.
        </p>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>SSO Endpoint</h2>
        <div className={styles.endpoint}>
          <div className={styles.endpointHeader}>
            <span className={`${styles.methodBadge} ${styles.methodGet}`}>GET</span>
            <code className={styles.endpointPath}>/sso</code>
          </div>
          <p className={styles.text}>
            Authenticates a user via HMAC-signed token. On success, redirects to the
            admin panel with an active session. On failure, redirects to an error page.
          </p>
        </div>

        <FieldTable title="Query Parameters" fields={SSO_QUERY_PARAMS} />
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Token Signing</h2>
        <p className={styles.text}>
          The token is an HMAC-SHA256 signature of a payload string, signed with your
          agency's SSO secret. The payload format is:
        </p>
        <CodeBlock label="Signing Formula">
{`HMAC-SHA256(email + ":" + unix_timestamp + ":" + agency_code, sso_secret)

Example payload: "user@partner.com:1712130000:AOB475"
Result: hex-encoded SHA-256 HMAC digest`}
        </CodeBlock>
        <ul className={styles.notesList}>
          <li>The timestamp must be the current Unix time in <strong>seconds</strong> (not milliseconds).</li>
          <li>The timestamp must be within <strong>5 minutes</strong> of the server's current time.</li>
          <li>The agency code must match your assigned agency code exactly (case-sensitive).</li>
          <li>Generate a fresh token for each SSO attempt — do not reuse tokens.</li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>SSO Code Examples</h2>
        <CodeBlock label="Bash / curl">{SSO_CURL_EXAMPLE}</CodeBlock>
        <CodeBlock label="Python">{SSO_PYTHON_EXAMPLE}</CodeBlock>
        <CodeBlock label="Node.js">{SSO_NODE_EXAMPLE}</CodeBlock>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Redirect Flow</h2>
        <p className={styles.text}>
          After validating the token, the endpoint redirects the user's browser:
        </p>
        <ul className={styles.notesList}>
          <li>
            <strong>Success:</strong> Redirects to <code className={styles.inlineCode}>/admin?sso_token=JWT</code>.
            The frontend stores the JWT and the user is logged in as an agency admin.
          </li>
          <li>
            <strong>Failure:</strong> Redirects to <code className={styles.inlineCode}>/sso-error?reason=ERROR_CODE</code> with
            a human-readable error message.
          </li>
          <li>
            <strong>New users:</strong> If the email does not exist in Oval, an agency admin account
            is automatically created and scoped to your agency.
          </li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Agency Admin Permissions</h2>
        <p className={styles.text}>
          Users authenticated via SSO receive the <code className={styles.inlineCode}>agency_admin</code> role,
          which is scoped to your agency:
        </p>
        <div className={styles.permissionsGrid}>
          <div className={styles.permCard}>
            <h3 className={styles.permTitle}>Can access</h3>
            <ul className={styles.notesList}>
              <li>Member list for your agency</li>
              <li>Individual member details</li>
              <li>Dashboard statistics for your agency</li>
            </ul>
          </div>
          <div className={styles.permCard}>
            <h3 className={styles.permTitle}>Cannot access</h3>
            <ul className={styles.notesList}>
              <li>Members from other agencies</li>
              <li>System settings or configuration</li>
              <li>Super admin features</li>
              <li>Other agencies' data</li>
            </ul>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>SSO Error Codes</h2>
        <ErrorCodeTable codes={SSO_ERROR_CODES} />
      </section>

      <footer className={styles.footer}>
        <p>Need help? Contact <a href="mailto:support@ovalcare.com">support@ovalcare.com</a></p>
      </footer>
    </div>
  </div>
);

export default ApiDocsPage;
